import * as React from 'react';
import { observer } from 'mobx-react';

import { translate, ITranslationsCollection } from 'elc-service';
import { Breakpoints } from 'elc-base-theme';
import { Desktop, Mobile, LargeScreen, ExtraLargeScreen } from 'elc-layouts';

import { ProductShadesSidebarButton } from '../../internal/views/product-shades-sidebar/ProductShadesSidebarButton';
import { ShadePickerCarousel } from '../../internal/views/product-shade-picker/ShadePickerCarousel';
import { ShadeGrid } from '../../internal/views/product-shade-picker/ShadeGrid';
import { ShadeFilterColumn } from '../../internal/views/product-shade-picker/ShadeFilterColumn';
import { SingleShade } from '../../internal/views/product-shade-picker/SingleShade';
import {
    SidebarButtonSection,
    SelectAShadeLabel
} from '../../internal/views/product-shade-picker/ProductShadePickerStyles';

import { IShadePicker, IShadeButtonForModal } from '../../internal/interfaces/IShadePicker';
import { ProductShadePickerViewController } from '../../internal/controllers/ProductShadePickerViewController';
import { SHADE_PICKER_TYPE } from '../../internal/constants/ShadePickerType';
import { classes } from '../../internal/utils/Classes';

const ShadeButtonForModal = ({ selectedShade, selectShadeLabel }: IShadeButtonForModal) => (
    <SidebarButtonSection>
        <SelectAShadeLabel>{selectShadeLabel}:</SelectAShadeLabel>
        <ProductShadesSidebarButton selectedShade={selectedShade} />
    </SidebarButtonSection>
);

@translate(['selectShadeLabel'])
@observer
export class ProductShadePicker extends React.Component<IShadePicker> {
    private viewController: ProductShadePickerViewController;

    constructor(props: IShadePicker) {
        super(props);
        this.viewController = props.viewController;
        this.viewController.initialize(props.config);
    }

    private getShadePickerGrid = (hasColorFamily: boolean, isMobile: boolean) => {
        const {
            selectedShade,
            separator,
            shadeSwatchesConfig = {},
            onShadeSelect,
            shades,
            productId,
            isPerfectShadeEnabled,
            perfectShades
        } = this.props.config;

        const { shadeName, shadeDescription } = shadeSwatchesConfig;

        const {
            hasMultipleShades,
            filteredShades,
            isShadeInfoInline,
            isShadeInfoAbovePicker,
            isShadeInfoBelowPicker
        } = this.viewController;

        const shadeConfig = {
            onShadeSelect,
            shades,
            selectedShade,
            productId,
            isPerfectShadeEnabled,
            perfectShades
        };

        return (
            <>
                {hasColorFamily && <ShadeFilterColumn />}
                <ShadeGrid
                    isShadeInfoInline={isShadeInfoInline}
                    selectedShade={selectedShade}
                    separator={separator}
                    shadeInfoAbovePicker={isShadeInfoAbovePicker}
                    shadeInfoBelowPicker={isShadeInfoBelowPicker}
                    shadeConfig={shadeConfig}
                    filteredShades={filteredShades}
                    shadeDescriptionPosition={shadeDescription}
                    shadeNamePosition={shadeName}
                    hasMultipleShades={hasMultipleShades}
                />
            </>
        );
    };

    private getShadePickerCarousel = () => {
        const {
            shades,
            selectedShade,
            separator,
            shadeSwatchesConfig = {},
            onShadeSelect,
            productId,
            isPerfectShadeEnabled,
            perfectShades
        } = this.props.config;

        const { shadeName, shadeDescription } = shadeSwatchesConfig;

        const {
            filteredShades,
            isShadeInfoAbovePicker,
            isShadeInfoBelowPicker,
            responsiveCarouselSettings,
            hasMultipleShades
        } = this.viewController;

        const shadeConfig = {
            onShadeSelect,
            shades,
            selectedShade,
            productId,
            isPerfectShadeEnabled,
            perfectShades
        };

        return hasMultipleShades ? (
            <ShadePickerCarousel
                hasMultipleShades={hasMultipleShades}
                selectedShade={selectedShade}
                shades={shades}
                separator={separator}
                shadeInfoAbovePicker={isShadeInfoAbovePicker}
                shadeInfoBelowPicker={isShadeInfoBelowPicker}
                sliderSettings={responsiveCarouselSettings}
                shadeDescriptionPosition={shadeDescription}
                shadeNamePosition={shadeName}
                filteredShades={filteredShades}
                shadeConfig={shadeConfig}
            />
        ) : (
            <SingleShade selectedShade={selectedShade} />
        );
    };

    public render() {
        const { selectedShade, isButtonModal, shadeSwatchesConfig } = this.props.config;

        const { breakpoints, ...config } = shadeSwatchesConfig || {};

        const { selectShadeLabel } = this.props.translations as ITranslationsCollection;
        const { hasColorFamily } = this.props.viewController;

        const sidebarButtonSectionClassNames = classes('sidebar-button-section');
        const productShadePickerClassNames = classes('product-shade-picker');

        if (isButtonModal) {
            return (
                <div className={sidebarButtonSectionClassNames}>
                    <ShadeButtonForModal
                        selectedShade={selectedShade}
                        selectShadeLabel={selectShadeLabel}
                    />
                </div>
            );
        }

        if (!shadeSwatchesConfig || !shadeSwatchesConfig.enabled) {
            return null;
        }
        const desktop = breakpoints && breakpoints.desktop;
        const largeScreen = breakpoints && breakpoints.largeScreen;
        const extraLargeScreen = breakpoints && breakpoints.extraLargeScreen;

        const desktopConfig = { ...config, ...desktop };
        const largeScreenConfig = { ...desktopConfig, ...largeScreen };
        const extraLargeScreenConfig = { ...largeScreenConfig, ...extraLargeScreen };

        return (
            <div className={productShadePickerClassNames}>
                <ExtraLargeScreen minWidth={Breakpoints.extraLargeScreen}>
                    {() =>
                        extraLargeScreenConfig.type === SHADE_PICKER_TYPE.GRID
                            ? this.getShadePickerGrid(hasColorFamily, false)
                            : this.getShadePickerCarousel()
                    }
                </ExtraLargeScreen>
                <LargeScreen
                    minWidth={Breakpoints.largeScreen}
                    maxWidth={Breakpoints.extraLargeScreen - 1}
                >
                    {() =>
                        largeScreenConfig.type === SHADE_PICKER_TYPE.GRID
                            ? this.getShadePickerGrid(hasColorFamily, false)
                            : this.getShadePickerCarousel()
                    }
                </LargeScreen>
                <Desktop minWidth={Breakpoints.desktop} maxWidth={Breakpoints.largeScreen - 1}>
                    {() =>
                        desktopConfig.type === SHADE_PICKER_TYPE.GRID
                            ? this.getShadePickerGrid(hasColorFamily, false)
                            : this.getShadePickerCarousel()
                    }
                </Desktop>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() =>
                        config.type === SHADE_PICKER_TYPE.GRID
                            ? this.getShadePickerGrid(hasColorFamily, true)
                            : this.getShadePickerCarousel()
                    }
                </Mobile>
            </div>
        );
    }
}

export default ProductShadePicker;
